import React, { useContext, useEffect, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { Box, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { NetworkTreeDto, NetworkTreeNode, NetworkTreeOption, NetworkTreeSearchResult } from '../../interfaces/IAffectedCustomers'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'
import { groupOptionsByCategory, NETWORK_NODE_CATEGORIES, networkNodeCategory } from './networkTreeSearch'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { AffectedEntitiesListDialog } from './AffectedEntitiesListDialog'
import { enqueueSnackbar } from 'notistack'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'

interface NetworkTreeSearchFieldProps {
  options: NetworkTreeOption[]
  disabled?: boolean
  selectedOptionsValue: NetworkTreeOption[]
  onSelectedOptions: (selectedOptions: NetworkTreeOption[]) => void
  loadingInitial: boolean
}

function instanceOfNetworkOption(object: any | any[]): object is NetworkTreeOption | NetworkTreeOption[] {
  if (Array.isArray(object)) {
    return object.every((item) => 'id' in item && 'label' in item)
  }

  return 'id' in object && 'label' in object
}

function NetworkTreeSearchField({ selectedOptionsValue, loadingInitial, onSelectedOptions, disabled = false }: NetworkTreeSearchFieldProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const [searchTerm, setSearchTerm] = useState('')

  const [networkTreeSearchResult, setNetworkTreeSearchResult] = useState<NetworkTreeSearchResult>()
  const [networkTreeOptions, setNetworkTreeOptions] = useState<NetworkTreeOption[]>([])
  const [selectedOptions, setSelectedOptions] = useState<NetworkTreeOption[]>(selectedOptionsValue)
  const [isLoading, setIsLoading] = useState(false)
  const [openAddedAffectedEntitiesDialog, setOpenAddedAffectedEntitiesDialog] = useState(false)
  const [openInvalidAffectedEntitiesDialog, setOpenInvalidAffectedEntitiesDialog] = useState(false)
  const [showValidAffectedEntities, setShowValidAffectedEntities] = useState(false)
  const [amountOfTags, setAmountOfTags] = useState(0)

  const optionsByCategory = useMemo(() => {
    const categories = new Map<string, NetworkTreeOption[]>()

    for (const option of networkTreeOptions) {
      const category = option.category ?? NETWORK_NODE_CATEGORIES.other
      const existing = categories.get(category)

      if (existing) existing.push(option)
      else categories.set(category, [option])
    }

    return categories
  }, [networkTreeOptions])

  const isOptionSelected = (option: NetworkTreeOption) => selectedOptions.some((selectedOption) => selectedOption.id === option.id)

  const handleShowAllEntities = () => {
    if (selectedOptions && selectedOptions.length <= 1000) {
      if (!showValidAffectedEntities) {
        setAmountOfTags(1000)
        setShowValidAffectedEntities(true)
      } else {
        setAmountOfTags(0)
        setShowValidAffectedEntities(false)
      }
    } else {
      setOpenAddedAffectedEntitiesDialog(true)
      setAmountOfTags(0)
      setShowValidAffectedEntities(false)
    }
  }

  const handleSelectedOptionChange = (event: React.ChangeEvent<{}>, selected: NetworkTreeOption[]) => {
    if (selected.length < selectedOptions.length) {
      setSelectedOptions(selected)
      return
    }

    const newOptionSelected = selected.filter((option) => !selectedOptions.some((selectedOption) => selectedOption.id === option.id))
    if (newOptionSelected.length !== 0) {
      setSelectedOptions((prevSelectedOptions) => [...prevSelectedOptions, ...newOptionSelected])
    }
  }

  const handleSelectAllInCategory = (category: string) => {
    const categoryOptions = optionsByCategory.get(category) ?? []

    if (categoryOptions.every(isOptionSelected)) {
      setSelectedOptions((prevSelectedOptions) =>
        prevSelectedOptions.filter((selectedOption) => !categoryOptions.some((option) => option.id === selectedOption.id)),
      )
      return
    }

    const addSelected = categoryOptions.filter((option) => !isOptionSelected(option))
    setSelectedOptions((prevSelectedOptions) => [...prevSelectedOptions, ...addSelected])
  }

  const createNetworkTreeOption = (networkTree: NetworkTreeDto | NetworkTreeNode): NetworkTreeOption => {
    const suffix = ` (${networkTree.model ? networkTree.model : networkTree.type})`
    const circuits = networkTree.circuitNumbers?.length ? networkTree.circuitNumbers.join(', ') : null
    const servicePointId = networkTree.servicePointReference || null

    let label: string
    if (networkTree.type === 'ServicePoint' && (circuits || servicePointId)) {
      const parts = [circuits, servicePointId].filter((p) => p !== null)
      label = `${parts.join(' / ')}${suffix}`
    } else if (networkTree.type === 'Equipment' && networkTree.managementIp) {
      label = `${networkTree.name} / ${networkTree.managementIp}${suffix}`
    } else {
      label = `${networkTree.name}${suffix}`
    }

    return {
      id: networkTree.id,
      label: label,
      category: networkNodeCategory(networkTree.type),
    }
  }

  const handleNetworkSearch = async (inputValue: string) => {
    // Empty terms (leading separator, or an empty field) make the backend report the
    // term as a failed search, so they are dropped before the request is built.
    const uniqueValues = inputValue
      .trim()
      .split(/[\s;,]+/)
      .filter((value) => value !== '')
      .filter((item, i, ar) => ar.indexOf(item) === i)

    setNetworkTreeOptions([])

    if (uniqueValues.length === 0) return

    setIsLoading(true)

    try {
      const response = await context.restApiClient
        .buildRequest('/api/network-tree/search')
        .serializeToJsonContent(uniqueValues)
        .deserializeResponseToJson<NetworkTreeSearchResult>()
        .putAsync()

      if (response.json) {
        const searchResult = response.json

        // One search returns every node type; the categories separate services from
        // equipment in the dropdown instead of a mode the user has to know about.
        const options = groupOptionsByCategory(
          searchResult.foundNodes
            .map((node: NetworkTreeNode) => createNetworkTreeOption(node))
            .filter((option) => !searchResult.failedNodes?.includes(option.label)),
        )

        setNetworkTreeSearchResult(searchResult)
        setNetworkTreeOptions(options)

        if (options.length === 0) {
          enqueueSnackbar(<Typography variant="body2">Fant ingen treff.</Typography>, { variant: 'info' })
        }

        return
      }

      if (response.error) {
        enqueueSnackbar(<Typography variant="body2">Kunne ikke søke. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setNetworkTreeOptions([])
      setIsLoading(false)
      return
    }

    const timeoutId = setTimeout(() => {
      handleNetworkSearch(searchTerm)
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    onSelectedOptions(selectedOptions)
  }, [selectedOptions])

  return (
    <>
      <AffectedEntitiesListDialog
        title={`Utstyr/tjenester lagt til totalt`}
        openDialog={openAddedAffectedEntitiesDialog}
        setOpenDialog={setOpenAddedAffectedEntitiesDialog}
        entities={selectedOptions.map((option) => option.label) || ['']}
      />
      <AffectedEntitiesListDialog
        title={`Søkeord uten treff`}
        openDialog={openInvalidAffectedEntitiesDialog}
        setOpenDialog={setOpenInvalidAffectedEntitiesDialog}
        entities={networkTreeSearchResult?.failedNodes || []}
      />
      <Stack direction={'column'}>
        <Stack direction={'row'} spacing={1}>
          <RouterOutlinedIcon />
          <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.primary'} sx={{
            alignSelf: "center"
          }}>
            Legg til utstyr/tjeneste {(disabled && ' (ikke mulig ved varsling til alle kunder)') || ''}
          </Typography>
          {loadingInitial && <LoadingIndicator size={'xsmall'} />}
        </Stack>
        <Autocomplete
          sx={{
            backgroundColor: 'background.paper',
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
          loading={isLoading}
          loadingText={
            <Stack direction="row" sx={{
              alignItems: "center"
            }}>
              <LoadingIndicator size={'xsmall'} />
              <Typography variant="body2">Søker...</Typography>
            </Stack>
          }
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'flip',
                  enabled: false,
                },
                {
                  name: 'preventOverflow',
                  enabled: false,
                },
              ],
            },
          }}
          limitTags={4}
          getLimitTagsText={(more) => `+${more} flere`}
          multiple
          disabled={disabled}
          freeSolo
          clearOnBlur={false}
          clearOnEscape={false}
          inputValue={searchTerm}
          onInputChange={(event, newInputValue, reason) => {
            // Only update searchTerm if user types or clears, not on option select
            if (reason === 'input' || reason === 'clear') {
              setSearchTerm(newInputValue)
            }
          }}
          disableCloseOnSelect
          noOptionsText="Ingen alternativer"
          options={networkTreeOptions}
          groupBy={(option: NetworkTreeOption) => option.category ?? NETWORK_NODE_CATEGORIES.other}
          value={selectedOptions}
          filterOptions={(x) => x}
          isOptionEqualToValue={(option: NetworkTreeOption, value: string | NetworkTreeOption) => {
            if (typeof value === 'string') return false
            return option.id === value.id
          }}
          getOptionLabel={(option: NetworkTreeOption | string) => {
            if (instanceOfNetworkOption(option)) {
              return option.label
            }

            return ''
          }}
          onChange={(event: React.ChangeEvent<{}>, value: (string | NetworkTreeOption)[]) => {
            if (instanceOfNetworkOption(value)) {
              handleSelectedOptionChange(event, value as NetworkTreeOption[])
            }
          }}
          renderValue={(value: (string | NetworkTreeOption)[], getItemProps) => (
            <Stack direction={'column'}>
              <Stack direction="row" spacing={1} sx={{
                alignItems: 'center'
              }}>
                <IconButton
                  size="small"
                  onClick={handleShowAllEntities}
                  aria-placeholder={showValidAffectedEntities ? 'Vis færre' : 'Vis alle'}
                  title={`Vis alle valgte`}
                >
                  {!showValidAffectedEntities ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <Stack direction="column">
                  {networkTreeSearchResult?.failedNodes && networkTreeSearchResult?.failedNodes.length > 0 && (
                    <Typography
                      variant="caption"
                      onClick={() => setOpenInvalidAffectedEntitiesDialog(true)}
                      sx={{
                        cursor: 'pointer',
                        color: 'error.main',
                      }}
                    >
                      {`${networkTreeSearchResult?.failedNodes.length} søkeord uten treff (siste søk)`}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    onClick={() => setOpenAddedAffectedEntitiesDialog(true)}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    {`${value.length} utstyr/tjenester lagt til totalt`}
                  </Typography>
                </Stack>
              </Stack>
              <Box>
                {value.length > 0 &&
                  value.slice(0, amountOfTags).map((option, index) => {
                    const { key, ...tagProps } = getItemProps({ index })

                    return (
                      <Chip
                        key={key}
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          borderColor: 'primary.main',
                        }}
                        label={option ? (option as NetworkTreeOption).label : ''}
                        {...tagProps}
                      />
                    )
                  })}
              </Box>
            </Stack>
          )}
          renderOption={(props, option) => {
            const isSelected = isOptionSelected(option)
            // Use index from Autocomplete's internal props for uniqueness
            const index = (props as any)['data-option-index'] ?? ''
            return (
              <li {...props} key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{option.label}</span>
                {isSelected && (
                  <Tooltip title="Valgt" placement="top">
                    <CheckIcon color="success" fontSize="small" style={{ marginLeft: 8 }} />
                  </Tooltip>
                )}
              </li>
            )
          }}
          renderGroup={(params) => {
            const categoryOptions = optionsByCategory.get(params.group) ?? []
            const selectedCount = categoryOptions.filter(isOptionSelected).length

            return (
              <li key={params.key}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: -8,
                    zIndex: 1,
                    pl: 1.5,
                    py: 0.5,
                    backgroundColor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    disabled={disabled}
                    disableTypography
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {`${params.group} (${categoryOptions.length})`}
                      </Typography>
                    }
                    control={
                      <Checkbox
                        size="small"
                        checked={categoryOptions.length > 0 && selectedCount === categoryOptions.length}
                        indeterminate={selectedCount > 0 && selectedCount < categoryOptions.length}
                        onChange={() => handleSelectAllInCategory(params.group)}
                      />
                    }
                  />
                </Box>
                <Box component="ul" sx={{ p: 0, '& .MuiAutocomplete-option': { pl: 3 } }}>
                  {params.children}
                </Box>
              </li>
            )
          }}
          renderInput={(params) => (
            <FormControl fullWidth disabled={disabled}>
              <TextField
                multiline
                defaultValue={searchTerm}
                value={searchTerm}
                disabled={disabled}
                placeholder="Splitt med , ; eller mellomrom"
                variant="outlined"
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter' || event.key === 'Backspace' || event.key === 'Delete') {
                    event.stopPropagation()
                  }
                }}
                slotProps={{
                  input: {
                    ...params.slotProps.input,
                    style: {
                      flexWrap: 'wrap-reverse',
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  },

                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    style: {
                      width: '100%',
                      maxHeight: '200px',
                      overflow: 'auto',
                    },
                  },

                  inputLabel: {
                    sx: {
                      fontSize: 'body2.fontSize',
                    },
                  }
                }} />
              {selectedOptions.length === 0 && (
                <FormHelperText>
                  <Typography variant="caption" color={disabled ? 'text.disabled' : 'text.primary'}>
                    Søk og legg til i nettverkstre
                  </Typography>
                </FormHelperText>
              )}
            </FormControl>
          )}
          slots={{
            paper: (paperProps) => (
              <Paper
                {...paperProps}
                sx={{
                  width: { xs: '90vw', sm: '550px' },
                  minWidth: 'unset',
                  maxWidth: '100vw',
                  boxSizing: 'border-box',
                }}
              />
            )
          }}
        />
      </Stack>
    </>
  );
}

export default NetworkTreeSearchField
